import { FabButton, IUser, List, SearchField } from '@localina/core';
import { AddIcon } from '@localina/icons';
import { useQueryClient } from '@tanstack/react-query';
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { queryKeys } from '../../api/queries/query-keys';
import { useGetRestaurants } from '../../api/queries/restaurants';
import { useUsers } from '../../api/queries/users';
import { Page, UserListItem } from '../../components';
import UserView from './UserView';

type TDrawerState = {
    open: boolean;
    userId: string;
};

const defaultDrawerState = {
    open: false,
    userId: 'new',
};

const Users: React.FC = () => {
    const { t } = useTranslation();
    const usersQuery = useUsers();
    const restaurantsQuery = useGetRestaurants();
    const queryClient = useQueryClient();
    const [drawerState, setDrawerState] = useState<TDrawerState>(defaultDrawerState);
    const [search, setSearch] = useState('');

    const handleEditUser = (item: IUser) => {
        queryClient.setQueryData(queryKeys.users.single(item.id), (oldData) => (oldData ? undefined : item));
        setDrawerState({ open: true, userId: item.id });
    };

    const handleCreateUser = () => {
        setDrawerState({ open: true, userId: 'new' });
    };

    const onCloseDialog = () => {
        setDrawerState(defaultDrawerState);
    };

    const users = usersQuery.data ?? [];
    const needle = search.trim().toLowerCase();
    const visibleUsers = needle
        ? users.filter((user) =>
              [user.firstName, user.lastName, user.email]
                  .filter(Boolean)
                  .some((field) => field.toLowerCase().includes(needle)),
          )
        : users;

    const restaurantCount = restaurantsQuery.data?.restaurants.length ?? 0;
    const subtitle = usersQuery.isSuccess
        ? needle
            ? t('users.subtitleFiltered', { shown: visibleUsers.length, total: users.length })
            : t('users.usersCount', { count: users.length })
        : undefined;

    return (
        <Page name="users" title={{ value: t('users.title'), subtitle }} isLoading={usersQuery.isInitialLoading}>
            <div className="users__filter">
                <SearchField label={t('users.search')} value={search} onChange={setSearch} onSearch={setSearch} />
            </div>
            <div className="users__list">
                <List>
                    {visibleUsers.map((user) => (
                        <UserListItem
                            key={user.id}
                            user={user}
                            restaurantCount={restaurantCount}
                            onClick={handleEditUser}
                        />
                    ))}
                </List>
                {usersQuery.isSuccess && visibleUsers.length === 0 && (
                    <div className="users__empty">{t('users.empty')}</div>
                )}
            </div>
            <FabButton icon={<AddIcon />} onClick={handleCreateUser} fixed />
            <UserView open={drawerState.open} onClose={onCloseDialog} userId={drawerState.userId} />
        </Page>
    );
};

export default Users;
